# Errors

All errors return a JSON body with at least `error` (machine-readable code) and `message`
(human-readable explanation). HTTP status follows REST conventions.

## Error shape

```json
{
  "error": "recipient_kyc_not_approved",
  "message": "recipient abc... is kyc_status='pending' — splits require approved",
  "statusCode": 400
}
```

When a payment was created but the provider rejected it, the intent persists with
`status='failed'` and the response carries `refusedCode` + `refusedReason`:

```json
{
  "id": "intent_xyz...",
  "status": "failed",
  "refusedCode": "insufficient_funds",
  "refusedReason": "cc_rejected_insufficient_amount"
}
```

## Normalized refused codes

Adapter-level normalization across providers:

| Code | Meaning |
|---|---|
| `insufficient_funds` | Card declined for amount |
| `high_risk` | Provider risk engine blocked |
| `invalid_cvv` | Wrong security code |
| `invalid_expiration` | Wrong exp date |
| `invalid_card_number` | BIN/length issue |
| `issuer_declined` | Card issuer refused (call-bank scenarios) |
| `unknown` | Provider returned a code we don't yet map |
| `routing_no_candidate` | No eligible provider account for this store + method + mode |
| `network_error` | Provider unreachable |
| `method_not_supported` | Provider/adapter doesn't expose this payment method |
| `invalid_card_input` | Neither `card.token` nor full raw fields present |
| `transfer_not_enabled_on_account` | MP Disbursements not enabled on this merchant |

## Validation errors (400)

Returned by `class-validator` when the request body is malformed:

```json
{
  "statusCode": 400,
  "message": [
    "amount must be a positive integer",
    "customer.email must be an email"
  ],
  "error": "Bad Request"
}
```

## Authentication errors (401)

| Status | Trigger |
|---|---|
| `401` | `Authorization` header missing or wrong format |
| `401` | Public key not found or secret didn't match |
| `401` | API key revoked or expired |

## Authorization errors (403)

| Code | Meaning |
|---|---|
| `customer_blocked` | The store admin blocked this customer (`POST /admin/customers/:id status='blocked'`) |
| `forbidden` (permission missing) | API key lacks `<permission>` for this route |

## State errors (400)

| Code | Meaning |
|---|---|
| `idempotency_conflict` | Same `X-N4-Idempotency-Key` used with a different body in the last 24h |
| `idempotency_in_flight` | Same key still processing (replay within 1h lock window) |
| `invalid_state` | E.g., trying to capture a non-`authorized` intent |
| `amount_exceeds_authorization` | Partial capture > auth amount |

## Split-specific errors (400)

| Code | Meaning |
|---|---|
| `recipient_not_found` | One or more split recipientIds don't exist in this org |
| `recipient_kyc_not_approved` | Recipient still in `pending`/`rejected` KYC |
| `recipient_not_active` | Recipient is `archived` |
| `split_recipient_unmapped` | Recipient has no provider mapping for the selected provider account |
| `mixed_split_mode` | Splits used both `amount` and `percentage` (must be all-or-nothing) |
| `missing_split_value` | Splits missing both `amount` and `percentage` |
| `duplicate_recipient` | Same `recipientId` appears twice |
| `split_percentage_out_of_range` | Sum of bps not in `(0, 10000]` |
| `split_sum_mismatch` | Sum of split amounts ≠ total (±1 cent tolerance) |
| `too_many_splits` | > 50 splits per transaction |

## Dispute / chargeback errors (400)

These come from the operator-facing endpoints under `/admin/chargebacks` and
`/admin/pix-med-cases`. Integrators don't trigger these directly; documented for operator
scripting and partner portals.

| Code | Meaning |
|---|---|
| `chargeback_not_found` | The chargeback id doesn't exist in the operator's org scope |
| `chargeback_already_disputed` | Operator already submitted a defense for this case |
| `chargeback_already_accepted` | Operator already accepted the loss — terminal |
| `chargeback_deadline_passed` | Provider's evidence deadline has expired; defense window closed |
| `chargeback_description_too_short` | Defense `description` is below the 20-char minimum |
| `pix_med_not_found` | The PIX MED case id doesn't exist in the operator's org scope |
| `pix_med_already_resolved` | Case is in a terminal state (`accepted`, `rejected`, or `reversed`) |
| `pix_med_intent_not_found` | When registering manually, the referenced `paymentIntentId` doesn't exist |

## Rate-limit + IP errors

| Status | Code | Meaning |
|---|---|---|
| `429` | `rate_limit_exceeded` | Per-key sliding window of 120 req / 60s on gateway endpoints. Header `Retry-After` indicates wait seconds |
| `401` | `ip_not_allowed` | When `api_keys.ip_whitelist` is set, request came from an IP outside the list |

## Super-admin / cross-org errors (403 + 400)

Returned by `/admin/super/*` endpoints (platform operator only) and by cross-org
operations elsewhere when the caller lacks `is_platform` membership.

| Code | Meaning |
|---|---|
| `super_admin_required` | Endpoint requires a user from the platform organization (`is_platform=true`) |
| `org_not_found` | Provided `organizationId` doesn't exist |
| `org_slug_conflict` | Slug already taken when creating an organization |
| `store_slug_conflict_within_org` | Slug already taken inside the same organization |
| `provider_account_not_assignable_cross_org` | Assignment failed because non-platform users can't span orgs |

## Server errors (5xx)

| Status | Meaning |
|---|---|
| `503 gateway_error` | Provider call threw unexpectedly; safe to retry with same idempotency key |
| `500` | Real unexpected error — auto-captured in N4 observability for ops review |
